import { PageHeader } from "../../components";
import { Divider, Table, Input } from "antd";
import { useMemo, useState } from "react";
import {
  getAllBills,
  getAllConsultations,
  getBranchExcuseDuties,
} from "../../helpers/api";
import { useNavigate } from "react-router-dom";
import { appLinks } from "../../helpers/config";
import { useQuery } from "react-query";
import { hasPermission } from "../../helpers/utilities";
import { action, resouces } from "../../helpers/auth";
import ExcuseDutySearch from "../../components/shared/excuse-duty-search";
import { Badge, Menu, Modal } from "@mantine/core";
import ConsultationComponent from "./components/consultations";
import BillsComponent from "./components/bills";
import NewBillForm from "../../components/billing/new-bill";
import { ExcuseDutyDisplay } from "../../components/shared/excuse-duty-display";
import { differenceInDays, format } from "date-fns";
import {
  IconReceipt2,
  IconReportMedical,
} from "@tabler/icons";

const CareIndex = (props) => {
  const [page, setPage] = useState(1);
  const [excuseSearch, setExcuseSearch] = useState("");

  const [modal, setModal] = useState({
    title: "",
    open: false,
    content: "",
    size: 500,
    zIndex: 55,
  });

  const nav = useNavigate();
  const canReadConsultations = hasPermission(
    resouces.consultations,
    action.canRead
  );
  const canCreateConsultations = hasPermission(
    resouces.consultations,
    action.canCreate
  );

  const {
    data: consultations,
    isFetching,
    refetch: fetchConsultations,
  } = useQuery({
    queryFn: getAllConsultations,
    queryKey: ["consultations"],
  });

  const {
    data: bills,
    isFetching: fetchingBills,
    refetch: fetchBills,
  } = useQuery({
    queryFn: getAllBills,
    queryKey: ["bills"],
  });

  const {
    data: excuseDuties = [],
    isFetching: fetchingExcuseDuties,
  } = useQuery({
    queryFn: getBranchExcuseDuties,
    queryKey: ["branch-excuse-duties"],
    enabled: canReadConsultations,
  });

  const filteredExcuseDuties = useMemo(() => {
    const q = excuseSearch.trim().toLowerCase();
    if (!q) {
      return excuseDuties;
    }
    return excuseDuties.filter((row) =>
      [row.memberName, row.gender, row.consultationCode].some((field) =>
        String(field ?? "")
          .toLowerCase()
          .includes(q)
      )
    );
  }, [excuseDuties, excuseSearch]);

  const openExcuseDutyDetail = (record) => {
    setModal({
      title: "Excuse duty",
      open: true,
      size: 960,
      zIndex: 55,
      content: (
        <ExcuseDutyDisplay
          key={record.id}
          code={record.consultationCode}
          excuseDutyId={record.id}
        />
      ),
    });
  };

  const excuseDutyColumns = [
    {
      title: "Member",
      dataIndex: "memberName",
      key: "memberName",
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
      width: 100,
    },
    {
      title: "Consultation code",
      dataIndex: "consultationCode",
      key: "consultationCode",
      render: (code) => <kbd>{code}</kbd>,
    },
    {
      title: "Start",
      dataIndex: "startDate",
      key: "startDate",
      render: (d) => (d ? format(new Date(d), "PP") : "—"),
    },
    {
      title: "End",
      dataIndex: "endDate",
      key: "endDate",
      render: (d) => (d ? format(new Date(d), "PP") : "—"),
    },
    {
      title: "Days",
      key: "days",
      width: 90,
      render: (_, record) => {
        if (!record.startDate || !record.endDate) {
          return "—";
        }
        const n = differenceInDays(
          new Date(record.endDate),
          new Date(record.startDate)
        );
        return Number.isFinite(n) ? n : "—";
      },
    },
    {
      title: "",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <button
          type="button"
          className="app-btn btn-text text-bold"
          onClick={() => openExcuseDutyDetail(record)}>
          Open
        </button>
      ),
    },
  ];

  return (
    <section>
      <Modal
        title={modal.title}
        opened={modal.open}
        size={modal.size}
        onClose={() => setModal({ ...modal, open: false })}
        zIndex={modal.zIndex}>
        {modal.content}
      </Modal>
      <PageHeader
        title="Care"
        hasBackButton
        description="member care management">
        <div className="d-flex flex-wrap gap-2 align-items-center">
          {canReadConsultations && (
            <button
              className="app-btn me-2"
              type="button"
              onClick={() =>
                setModal({
                  title: "Find Excuse Duty",
                  open: true,
                  content: <ExcuseDutySearch />,
                })
              }>
              <span className="bi bi-search me-2"></span>
              <span className="d-none d-md-inline"> Find Excuse Duty</span>
            </button>
          )}
          {canCreateConsultations && (
            <Menu
              trigger="hover"
              withArrow
              transition="scale-y"
              shadow="md"
              width={150}
              className="d-block">
              <Menu.Target>
                <button type="button" className="app-btn btn-prim fw-bold">
                  <span className="bi bi-plus-circle me-2"></span>
                  NEW
                </button>
              </Menu.Target>

              <Menu.Dropdown mr={50}>
                <Menu.Item
                  onClick={() => nav(appLinks.startConsultation.index)}>
                  <IconReportMedical
                    size={20}
                    color="grey"
                    className="me-2"
                  />
                  Consultation
                </Menu.Item>
                <Menu.Item
                  onClick={() =>
                    setModal({
                      title: "New Bill",
                      content: <NewBillForm onSuccess={fetchBills} />,
                      open: true,
                      size: 1200,
                      zIndex: 55,
                    })
                  }>
                  <IconReceipt2 size={20} color="grey" className="me-2" />
                  Bill
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
        </div>
      </PageHeader>
      <Divider className="mt-1" />
      <div className="buttons has-addons mb-3">
        <button
          type="button"
          className={`button ${page === 1 ? " is-selected" : ""}`}
          onClick={() => setPage(1)}>
          Visits
          <Badge className="ms-2" color="gray" variant="light">
            {consultations?.length || 0}
          </Badge>
        </button>
        <button
          type="button"
          className={`button ${page === 2 ? " is-selected" : ""}`}
          onClick={() => setPage(2)}>
          Bills
          <Badge className="ms-2" color="gray" variant="light">
            {bills?.length || 0}
          </Badge>
        </button>
        {canReadConsultations && (
          <button
            type="button"
            className={`button ${page === 3 ? " is-selected" : ""}`}
            onClick={() => setPage(3)}>
            Excuse Duties
            <Badge className="ms-2" color="gray" variant="light">
              {excuseDuties?.length || 0}
            </Badge>
          </button>
        )}
      </div>

      {page === 1 && (
        <ConsultationComponent
          consultations={consultations || []}
          onReload={fetchConsultations}
          isloading={isFetching}
        />
      )}

      {page === 2 && (
        <BillsComponent
          bills={bills}
          isloading={fetchingBills}
          onReload={fetchBills}
        />
      )}

      {page === 3 && canReadConsultations && (
        <>
          <Input
            allowClear
            className="mb-3"
            placeholder="Search by member name, gender, or consultation code..."
            value={excuseSearch}
            onChange={(e) => setExcuseSearch(e.target.value)}
          />
          <Table
            rowKey="id"
            loading={fetchingExcuseDuties}
            dataSource={filteredExcuseDuties}
            columns={excuseDutyColumns}
            pagination={{ pageSize: 15 }}
          />
        </>
      )}
    </section>
  );
};

export { CareIndex };
